import _extends from "@babel/runtime/helpers/extends";
import React, { useEffect, useRef } from "react";
import { UiConstants } from "../../data/UiConstants";
import styled, { css } from "styled-components";
import { observer } from "mobx-react-lite";
import { MathE } from "../../../shared/helper/MathExtension";
import { useEffectOnlyWhenDependenciesChange } from "../../helper/reactHelpers";
export let Orientation;

(function (Orientation) {
  Orientation[Orientation["Top"] = 0] = "Top";
  Orientation[Orientation["Right"] = 1] = "Right";
  Orientation[Orientation["Bottom"] = 2] = "Bottom";
  Orientation[Orientation["Left"] = 3] = "Left";
})(Orientation || (Orientation = {}));

export const Overlay = styled.div.withConfig({
  displayName: "PopupSubMenu__Overlay",
  componentId: "sc-1su4jrx-0"
})(["z-index:", ";position:fixed;left:0;right:0;top:0;bottom:0;background-color:rgba(0,0,0,0.5);"], UiConstants.Z_INDEX_POPUP_SUBMENU);
export const Submenu = styled.div.withConfig({
  displayName: "PopupSubMenu__Submenu",
  componentId: "sc-1su4jrx-1"
})(["border:2px solid black;background:white;padding:10px;height:100%;"]);
const PopupContainer = styled.div.withConfig({
  displayName: "PopupSubMenu__PopupContainer",
  componentId: "sc-1su4jrx-2"
})(["z-index:", ";position:", ";transform:", ";margin-right:", ";width:", ";", ";visibility:", ";left:", ";top:", ";"], UiConstants.Z_INDEX_POPUP_SUBMENU + 1, props => props.positionFixed ? "fixed" : "absolute", props => props.transform ? props.transform : 'unset', props => props.positionFixed ? undefined : "-50%", props => props.containerWidth ? props.containerWidth : '100%', props => props.containerWidth && css(["height:", ""], props.containerHeight), props => props.visible ? 'unset' : 'hidden', props => props.left, props => props.top);
export const DefaultButton = styled.button.withConfig({
  displayName: "PopupSubMenu__DefaultButton",
  componentId: "sc-1su4jrx-3"
})(["cursor:pointer;display:flex;&.invalid{outline:2px solid ", ";border-radius:2px;border-color:rgba(0,0,0,0);}"], UiConstants.COLOR_VALUE_INVALID);
export const PopupSubMenuIconContainer = styled.div.withConfig({
  displayName: "PopupSubMenu__PopupSubMenuIconContainer",
  componentId: "sc-1su4jrx-4"
})(["margin-right:4px;"]); // PopupSubMenu must be an observer() because props.childrenWithCallbacks might contain observable values

export const PopupSubMenuExternalState = observer(props => {
  const {
    isOpen,
    setIsOpen
  } = props;
  const [transform, setTransform] = React.useState('translate(0, 0)');
  const [visible, setVisibility] = React.useState(false);
  const [left, setLeft] = React.useState(undefined);
  const [top, setTop] = React.useState(undefined);
  const buttonRef = useRef(null);
  const popupRef = useRef(null);
  const relativeOffset = props.relativeOffset !== undefined ? props.relativeOffset : 0.5;
  const pixelOffsetX = props.pixelOffsetX !== undefined ? props.pixelOffsetX : 0;
  const pixelOffsetY = props.pixelOffsetY !== undefined ? props.pixelOffsetY : 0;

  function openPopup() {
    setIsOpen(true);
  }

  function closePopup() {
    setIsOpen(false);
  }

  useEffectOnlyWhenDependenciesChange(() => {
    if (isOpen) {
      if (props.onOpen) props.onOpen();
    } else {
      if (props.onClose) props.onClose();
    }
  }, [isOpen]);
  useEffect(() => {
    if (!isOpen) return;
    const buttonRect = buttonRef.current.getBoundingClientRect();
    const popupRect = popupRef.current.getBoundingClientRect();

    if (props.positionFixed) {
      setLeft(buttonRect.x + "px");
      setTop(MathE.clamp(buttonRect.y + buttonRect.height, 0, window.innerHeight - popupRect.height) + "px");

      if (props.orientation === Orientation.Left) {
        setTransform(`translate(calc(-100% + ${pixelOffsetX}px), calc(-${relativeOffset * 100}% + ${pixelOffsetY - buttonRect.height}px))`);
      } else if (props.orientation === Orientation.Right) {
        setTransform(`translate(${pixelOffsetX + buttonRect.width}px, calc(-${relativeOffset * 100}% + ${pixelOffsetY}px))`);
      } else if (props.orientation === Orientation.Top) {
        setTransform(`translate(calc(-${relativeOffset * 100}% + ${pixelOffsetX}px), calc(-100% + ${pixelOffsetY - buttonRect.height}px))`);
      } else if (props.orientation === Orientation.Bottom) {
        setTransform(`translate(calc(-${relativeOffset * 100}% + ${pixelOffsetX}px), ${pixelOffsetY}px)`);
      }
    } else {
      setLeft(undefined);
      setTop(undefined);
      let offsetY = pixelOffsetY;

      if (props.orientation === Orientation.Left || props.orientation === Orientation.Right) {
        offsetY += popupRect.height / 2 - popupRect.height * relativeOffset - buttonRect.height / 2 + buttonRect.height * relativeOffset;
      }

      let offsetX = pixelOffsetX;

      if (props.orientation === Orientation.Top || props.orientation === Orientation.Bottom) {
        offsetX += popupRect.width / 2 - popupRect.width * relativeOffset - buttonRect.width / 2 + buttonRect.width * relativeOffset;
      }

      if (popupRect.width === 0) return;

      if (props.orientation === Orientation.Left) {
        setTransform(`translate(${popupRect.width * -1 + offsetX}px, -${popupRect.height / 2 + buttonRect.height / 2 - offsetY}px)`);
      } else if (props.orientation === Orientation.Right) {
        setTransform(`translate(${buttonRect.width + offsetX}px, -${popupRect.height / 2 + buttonRect.height / 2 - offsetY}px)`);
      } else if (props.orientation === Orientation.Top) {
        setTransform(`translate(-${popupRect.width / 2 - buttonRect.width / 2 - offsetX}px, -${popupRect.height + buttonRect.height - offsetY}px)`);
      } else if (props.orientation === Orientation.Bottom) {
        setTransform(`translate(-${popupRect.width / 2 - buttonRect.width / 2 - offsetX}px, ${offsetY}px)`);
      }
    }

    setVisibility(true);
  }, [isOpen]);
  const buttonClass = {
    class: props.buttonClass || DefaultButton
  };
  return /*#__PURE__*/React.createElement("div", null, isOpen && /*#__PURE__*/React.createElement(Overlay, {
    onClick: closePopup
  }), /*#__PURE__*/React.createElement(buttonClass.class, {
    disabled: props.disabled,
    onClick: openPopup,
    ref: buttonRef,
    className: props.buttonInvalid ? "invalid" : ""
  }, props.buttonContent), /*#__PURE__*/React.createElement(PopupContainer, {
    transform: transform,
    visible: isOpen && visible,
    ref: popupRef,
    containerWidth: props.containerWidth,
    containerHeight: props.containerHeight,
    positionFixed: props.positionFixed,
    left: left,
    top: top
  }, isOpen && /*#__PURE__*/React.createElement(Submenu, null, props.childrenWithCallbacks ? props.childrenWithCallbacks(closePopup) : props.children)));
});
export const PopupSubMenu = props => {
  const [isOpen, setIsOpen] = React.useState(false);
  return /*#__PURE__*/React.createElement(PopupSubMenuExternalState, _extends({
    isOpen: isOpen,
    setIsOpen: setIsOpen
  }, props));
};